import React, { useState, useEffect } from 'react';
import { FiUsers, FiTrendingUp, FiAward, FiTarget, FiBarChart2 } from 'react-icons/fi';
import { formatINR } from '../../utils/currency';

const UserPerformance = () => {
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [performanceData, setPerformanceData] = useState([
    {
      id: 1,
      name: 'John Doe',
      employeeId: 'EMP001',
      department: 'Sales',
      role: 'Sales Executive',
      salesTarget: 50000,
      salesAchieved: 45000,
      ordersProcessed: 45,
      customerSatisfaction: 4.5,
      rating: 90
    },
    {
      id: 2,
      name: 'Jane Smith',
      employeeId: 'EMP002',
      department: 'Sales',
      role: 'Sales Manager',
      salesTarget: 75000,
      salesAchieved: 72000,
      ordersProcessed: 68,
      customerSatisfaction: 4.8,
      rating: 96
    },
    {
      id: 3,
      name: 'Mike Johnson',
      employeeId: 'EMP003',
      department: 'Production',
      role: 'Production Supervisor',
      salesTarget: 0,
      salesAchieved: 0,
      ordersProcessed: 0,
      productionEfficiency: 95,
      qualityScore: 98,
      rating: 96.5
    }
  ]);

  const topPerformers = performanceData
    .sort((a, b) => b.rating - a.rating)
    .slice(0, 5);

  const filteredData = selectedDepartment === 'all' 
    ? performanceData 
    : performanceData.filter(emp => emp.department.toLowerCase() === selectedDepartment.toLowerCase());

  const getRatingColor = (rating) => {
    if (rating >= 90) return 'text-green-600 bg-green-100';
    if (rating >= 75) return 'text-blue-600 bg-blue-100';
    if (rating >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <FiUsers className="text-blue-600" />
            User Performance
          </h1>
          <p className="text-gray-600 mt-1">Employee performance tracking and analytics</p>
        </div>
        <select
          value={selectedDepartment}
          onChange={(e) => setSelectedDepartment(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Departments</option>
          <option value="sales">Sales</option>
          <option value="production">Production</option>
          <option value="quality">Quality Control</option>
          <option value="admin">Administration</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Employees</p>
              <p className="text-2xl font-bold text-gray-800">{performanceData.length}</p>
            </div>
            <FiUsers className="text-blue-600 text-2xl" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Top Performers</p>
              <p className="text-2xl font-bold text-green-600">{topPerformers.length}</p>
            </div>
            <FiAward className="text-green-600 text-2xl" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Avg Rating</p>
              <p className="text-2xl font-bold text-purple-600">
                {(performanceData.reduce((sum, emp) => sum + emp.rating, 0) / performanceData.length).toFixed(1)}%
              </p>
            </div>
            <FiTrendingUp className="text-purple-600 text-2xl" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">On Target</p>
              <p className="text-2xl font-bold text-orange-600">
                {performanceData.filter(emp => emp.rating >= 75).length}
              </p>
            </div>
            <FiTarget className="text-orange-600 text-2xl" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Performance Overview</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Employee</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Department</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Performance</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Rating</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((emp) => (
                  <tr key={emp.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{emp.name}</p>
                        <p className="text-xs text-gray-500">{emp.employeeId}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{emp.department}</td>
                    <td className="px-4 py-3">
                      {emp.salesTarget > 0 ? (
                        <div>
                          <p className="text-xs text-gray-600">
                            Sales: {formatINR(emp.salesAchieved)} / {formatINR(emp.salesTarget)}
                          </p>
                          <p className="text-xs text-gray-600">Orders: {emp.ordersProcessed}</p>
                        </div>
                      ) : (
                        <div>
                          <p className="text-xs text-gray-600">Efficiency: {emp.productionEfficiency}%</p>
                          <p className="text-xs text-gray-600">Quality: {emp.qualityScore}%</p>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRatingColor(emp.rating)}`}>
                        {emp.rating}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Top Performers</h2>
          <div className="space-y-4">
            {topPerformers.map((emp, index) => (
              <div key={emp.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                  index === 0 ? 'bg-yellow-100 text-yellow-600' :
                  index === 1 ? 'bg-gray-100 text-gray-600' :
                  index === 2 ? 'bg-orange-100 text-orange-600' :
                  'bg-blue-100 text-blue-600'
                }`}>
                  {index + 1}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{emp.name}</p>
                  <p className="text-xs text-gray-500">{emp.department}</p>
                </div>
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${getRatingColor(emp.rating)}`}>
                  {emp.rating}%
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserPerformance;

